/**
 * PROFILE EDITOR COMPONENT (P0.5)
 * ===============================
 * 
 * Functional profile editing with:
 * - First name / Last name
 * - Email change (requires password confirmation)
 * - Password change
 * - Avatar display (prepared for future evolution)
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Mail, 
  Lock, 
  Eye, 
  EyeOff, 
  Check, 
  AlertCircle,
  ArrowLeft,
  Camera
} from 'lucide-react';
import axios from 'axios';
import { MemberAvatar } from './MemberAvatar';
import ProfilePhotoSheet from './ProfilePhotoSheet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// =============================================================================
// PROFILE EDITOR
// =============================================================================

const ProfileEditor = ({ 
  user, 
  onUpdate, 
  onClose, 
  lang = 'en' 
}) => {
  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI states
  const [activeSection, setActiveSection] = useState(null); // 'name', 'email', 'password'
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Phase 6 — the member's own picture, set from the same place they edit their name.
  const [showPhotoSheet, setShowPhotoSheet] = useState(false);

  // Translations
  const t = {
    en: {
      editProfile: 'Edit Profile',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
      save: 'Save',
      cancel: 'Cancel',
      changeEmail: 'Change Email',
      changePassword: 'Change Password',
      emailRequiresPassword: 'Password required to change email',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 8 characters',
      successName: 'Name updated successfully',
      successEmail: 'Email updated successfully',
      successPassword: 'Password changed successfully',
      errorGeneric: 'Something went wrong. Please try again.',
      personalInfo: 'Personal Information',
      security: 'Security',
      avatarHint: 'Your avatar will evolve as you grow',
      changePhoto: 'Change Photo'
    },
    es: {
      editProfile: 'Editar Perfil',
      firstName: 'Nombre',
      lastName: 'Apellido',
      email: 'Correo Electrónico',
      currentPassword: 'Contraseña Actual',
      newPassword: 'Nueva Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      save: 'Guardar',
      cancel: 'Cancelar',
      changeEmail: 'Cambiar Correo',
      changePassword: 'Cambiar Contraseña',
      emailRequiresPassword: 'Contraseña requerida para cambiar correo',
      passwordMismatch: 'Las contraseñas no coinciden',
      passwordTooShort: 'La contraseña debe tener al menos 8 caracteres',
      successName: 'Nombre actualizado correctamente',
      successEmail: 'Correo actualizado correctamente',
      successPassword: 'Contraseña cambiada correctamente',
      errorGeneric: 'Algo salió mal. Por favor, intenta de nuevo.',
      personalInfo: 'Información Personal',
      security: 'Seguridad',
      avatarHint: 'Tu avatar evolucionará a medida que crezcas',
      changePhoto: 'Cambiar foto'
    }
  }[lang];

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      // Parse name into first/last
      const nameParts = (user.name || '').split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  // Handle name update
  const handleNameUpdate = async () => {
    setLoading(true);
    setError('');
    try {
      const fullName = `${firstName} ${lastName}`.trim();
      await axios.put(`${API}/auth/profile`, { name: fullName });
      setSuccess(t.successName);
      if (onUpdate) onUpdate({ ...user, name: fullName });
      setActiveSection(null);
    } catch (err) {
      setError(err.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // Handle email update (requires password)
  const handleEmailUpdate = async () => {
    if (!currentPassword) {
      setError(t.emailRequiresPassword);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.put(`${API}/auth/profile/email`, { 
        email, 
        password: currentPassword 
      });
      setSuccess(t.successEmail);
      setCurrentPassword('');
      if (onUpdate) onUpdate({ ...user, email });
      setActiveSection(null);
    } catch (err) {
      setError(err.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setError(t.passwordMismatch);
      return;
    }
    // Match server-side validation: min 10 chars, uppercase, lowercase, number
    if (newPassword.length < 10) {
      setError(lang === 'es' 
        ? 'La contraseña debe tener al menos 10 caracteres con mayúscula, minúscula y número'
        : 'Password must be at least 10 characters with uppercase, lowercase, and number');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError(lang === 'es' 
        ? 'La contraseña debe incluir mayúscula, minúscula y número'
        : 'Password must include uppercase, lowercase, and number');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      setSuccess(t.successPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setActiveSection(null);
    } catch (err) {
      setError(err.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="profile-editor" data-testid="profile-editor">
      {/* Header */}
      <header className="profile-editor__header">
        <button 
          onClick={onClose}
          className="profile-editor__back"
          data-testid="profile-editor-back"
        >
          <ArrowLeft size={24} />
        </button>
        <h1 className="profile-editor__title">{t.editProfile}</h1>
        <div style={{ width: 24 }} /> {/* Spacer for centering */}
      </header>

      {/* Avatar Section */}
      <div className="profile-editor__avatar-section">
        <div className="profile-editor__avatar-wrapper">
          <MemberAvatar user={user} size={100} />
        </div>
        <button
          type="button"
          className="profile-editor__avatar-change"
          onClick={() => setShowPhotoSheet(true)}
          data-testid="profile-editor-change-photo"
        >
          <Camera size={14} />
          <span>{t.changePhoto}</span>
        </button>
      </div>

      {showPhotoSheet && (
        <ProfilePhotoSheet
          lang={lang}
          hasPhoto={!!user?.profile_photo?.photo_id}
          onSaved={async () => { if (onUpdate) await onUpdate(user); }}
          onRemoved={async () => { if (onUpdate) await onUpdate(user); }}
          onClose={() => setShowPhotoSheet(false)}
        />
      )}

      {/* Success/Error Messages */}
      <AnimatePresence>
        {(success || error) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`profile-editor__message ${error ? 'profile-editor__message--error' : ''}`}
          >
            {error ? <AlertCircle size={16} /> : <Check size={16} />}
            <span>{error || success}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Personal Information Section */}
      <section className="profile-editor__section">
        <h2 className="profile-editor__section-title">{t.personalInfo}</h2>
        
        {/* Name Fields */}
        <div 
          className={`profile-editor__field-group ${activeSection === 'name' ? 'active' : ''}`}
          onClick={() => !activeSection && setActiveSection('name')}
        >
          <div className="profile-editor__field">
            <User size={20} className="profile-editor__field-icon" />
            <div className="profile-editor__field-content">
              <label className="profile-editor__label">{t.firstName}</label>
              {activeSection === 'name' ? (
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="profile-editor__input"
                  autoFocus
                  data-testid="input-first-name"
                />
              ) : (
                <span className="profile-editor__value">{firstName || '-'}</span>
              )}
            </div>
          </div>
          
          <div className="profile-editor__field">
            <div style={{ width: 20 }} /> {/* Spacer */}
            <div className="profile-editor__field-content">
              <label className="profile-editor__label">{t.lastName}</label>
              {activeSection === 'name' ? (
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="profile-editor__input"
                  data-testid="input-last-name"
                />
              ) : (
                <span className="profile-editor__value">{lastName || '-'}</span>
              )}
            </div>
          </div>

          {activeSection === 'name' && (
            <div className="profile-editor__actions">
              <button 
                onClick={() => setActiveSection(null)}
                className="profile-editor__btn profile-editor__btn--secondary"
              >
                {t.cancel}
              </button>
              <button 
                onClick={handleNameUpdate}
                className="profile-editor__btn profile-editor__btn--primary"
                disabled={loading}
                data-testid="save-name-btn"
              >
                {loading ? '...' : t.save}
              </button>
            </div>
          )}
        </div>

        {/* Email Field */}
        <div 
          className={`profile-editor__field-group ${activeSection === 'email' ? 'active' : ''}`}
          onClick={() => !activeSection && setActiveSection('email')}
        >
          <div className="profile-editor__field">
            <Mail size={20} className="profile-editor__field-icon" />
            <div className="profile-editor__field-content">
              <label className="profile-editor__label">{t.email}</label>
              {activeSection === 'email' ? (
                <>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="profile-editor__input"
                    autoFocus
                    data-testid="input-email"
                  />
                  <p className="profile-editor__hint">{t.emailRequiresPassword}</p>
                  <div className="profile-editor__password-input">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t.currentPassword}
                      className="profile-editor__input"
                      data-testid="input-current-password-email"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="profile-editor__password-toggle"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </>
              ) : (
                <span className="profile-editor__value">{email}</span>
              )}
            </div>
          </div>

          {activeSection === 'email' && (
            <div className="profile-editor__actions">
              <button 
                onClick={() => { setActiveSection(null); setCurrentPassword(''); }}
                className="profile-editor__btn profile-editor__btn--secondary"
              >
                {t.cancel}
              </button>
              <button 
                onClick={handleEmailUpdate}
                className="profile-editor__btn profile-editor__btn--primary"
                disabled={loading || !currentPassword}
                data-testid="save-email-btn"
              >
                {loading ? '...' : t.save}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Security Section */}
      <section className="profile-editor__section">
        <h2 className="profile-editor__section-title">{t.security}</h2>
        
        {/* Password Change */}
        <div 
          className={`profile-editor__field-group ${activeSection === 'password' ? 'active' : ''}`}
          onClick={() => !activeSection && setActiveSection('password')}
        >
          <div className="profile-editor__field">
            <Lock size={20} className="profile-editor__field-icon" />
            <div className="profile-editor__field-content">
              <label className="profile-editor__label">{t.changePassword}</label>
              {activeSection === 'password' ? (
                <>
                  {/* Current Password */}
                  <div className="profile-editor__password-input">
                    <input
                      type={showCurrentPassword ? 'text' : 'password'}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder={t.currentPassword}
                      className="profile-editor__input"
                      autoFocus
                      data-testid="input-current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="profile-editor__password-toggle"
                    >
                      {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* New Password */}
                  <div className="profile-editor__password-input">
                    <input
                      type={showNewPassword ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder={t.newPassword}
                      className="profile-editor__input"
                      data-testid="input-new-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="profile-editor__password-toggle"
                    >
                      {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  
                  {/* Confirm Password */}
                  <div className="profile-editor__password-input">
                    <input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t.confirmPassword}
                      className="profile-editor__input"
                      data-testid="input-confirm-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="profile-editor__password-toggle"
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </>
              ) : (
                <span className="profile-editor__value">••••••••</span>
              )}
            </div>
          </div>

          {activeSection === 'password' && (
            <div className="profile-editor__actions">
              <button 
                onClick={() => { 
                  setActiveSection(null); 
                  setCurrentPassword(''); 
                  setNewPassword(''); 
                  setConfirmPassword(''); 
                }}
                className="profile-editor__btn profile-editor__btn--secondary"
              >
                {t.cancel}
              </button>
              <button 
                onClick={handlePasswordChange}
                className="profile-editor__btn profile-editor__btn--primary"
                disabled={loading || !currentPassword || !newPassword || !confirmPassword}
                data-testid="save-password-btn"
              >
                {loading ? '...' : t.save}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Styles */}
      <style>{`
        .profile-editor {
          min-height: 100vh;
          background: var(--bg-primary, #0A0A0F);
          color: #FFFFFF;
        }
        
        .profile-editor__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: calc(env(safe-area-inset-top, 47px) + 16px) 20px 16px;
          background: linear-gradient(to bottom, rgba(10, 10, 15, 0.98) 0%, transparent 100%);
          position: sticky;
          top: 0;
          z-index: 10;
        }
        
        .profile-editor__back {
          background: transparent;
          border: none;
          color: var(--color-gold-primary, #D4AF37);
          cursor: pointer;
          padding: 8px;
          margin: -8px;
        }
        
        .profile-editor__title {
          font-size: 18px;
          font-weight: 600;
          color: #FFFFFF;
        }
        
        .profile-editor__avatar-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 24px 20px 32px;
        }
        
        .profile-editor__avatar-wrapper {
          position: relative;
        }
        
        .profile-editor__avatar-edit {
          position: absolute;
          bottom: 0;
          right: 0;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: var(--color-gold-primary, #D4AF37);
          border: 2px solid var(--bg-primary, #0A0A0F);
          color: #000;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0.5;
          transition: opacity 0.2s;
        }
        
        .profile-editor__avatar-edit:hover {
          opacity: 0.7;
        }
        
        .profile-editor__avatar-hint {
          margin-top: 12px;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.4);
          font-style: italic;
        }
        
        .profile-editor__avatar-change {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          margin-top: 14px;
          padding: 9px 16px;
          border-radius: 999px;
          background: rgba(212, 175, 55, 0.1);
          border: 1px solid rgba(212, 175, 55, 0.32);
          color: var(--color-gold-primary, #D4AF37);
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: background-color 0.2s ease, border-color 0.2s ease;
        }
        
        .profile-editor__avatar-change:hover {
          background: rgba(212, 175, 55, 0.18);
          border-color: rgba(212, 175, 55, 0.5);
        }
        
        .profile-editor__message {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0 20px 16px;
          padding: 12px 16px;
          border-radius: 12px;
          background: rgba(76, 175, 80, 0.15);
          border: 1px solid rgba(76, 175, 80, 0.3);
          color: #81C784;
          font-size: 14px;
        }
        
        .profile-editor__message--error {
          background: rgba(244, 67, 54, 0.15);
          border-color: rgba(244, 67, 54, 0.3);
          color: #EF9A9A;
        }
        
        .profile-editor__section {
          padding: 0 20px 24px;
        }
        
        .profile-editor__section-title {
          font-size: 12px;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
        }
        
        .profile-editor__field-group {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 16px;
          padding: 16px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .profile-editor__field-group:hover:not(.active) {
          background: rgba(255, 255, 255, 0.05);
          border-color: rgba(255, 255, 255, 0.12);
        }
        
        .profile-editor__field-group.active {
          background: rgba(212, 175, 55, 0.05);
          border-color: rgba(212, 175, 55, 0.2);
          cursor: default;
        }
        
        .profile-editor__field {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .profile-editor__field:last-of-type {
          margin-bottom: 0;
        }
        
        .profile-editor__field-icon {
          color: rgba(255, 255, 255, 0.5);
          margin-top: 2px;
          flex-shrink: 0;
        }
        
        .profile-editor__field-content {
          flex: 1;
        }
        
        .profile-editor__label {
          display: block;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.5);
          margin-bottom: 4px;
        }
        
        .profile-editor__value {
          font-size: 16px;
          color: #FFFFFF;
        }
        
        .profile-editor__input {
          width: 100%;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 16px;
          color: #FFFFFF;
          outline: none;
          transition: border-color 0.2s;
        }
        
        .profile-editor__input:focus {
          border-color: var(--color-gold-primary, #D4AF37);
        }
        
        .profile-editor__input::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }
        
        .profile-editor__hint {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.4);
          margin: 8px 0;
        }
        
        .profile-editor__password-input {
          position: relative;
          margin-top: 8px;
        }
        
        .profile-editor__password-input .profile-editor__input {
          padding-right: 44px;
        }
        
        .profile-editor__password-toggle {
          position: absolute;
          right: 12px;
          top: 50%;
          transform: translateY(-50%);
          background: transparent;
          border: none;
          color: rgba(255, 255, 255, 0.5);
          cursor: pointer;
          padding: 4px;
        }
        
        .profile-editor__actions {
          display: flex;
          gap: 12px;
          margin-top: 16px;
          padding-top: 16px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
        }
        
        .profile-editor__btn {
          flex: 1;
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .profile-editor__btn--primary {
          background: var(--color-gold-primary, #D4AF37);
          border: none;
          color: #000;
        }
        
        .profile-editor__btn--primary:hover:not(:disabled) {
          background: #E8C56A;
        }
        
        .profile-editor__btn--primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .profile-editor__btn--secondary {
          background: transparent;
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #FFFFFF;
        }
        
        .profile-editor__btn--secondary:hover {
          border-color: rgba(255, 255, 255, 0.4);
        }
      `}</style>
    </div>
  );
};

export default ProfileEditor;
